import { $api } from '@/services';
import { getFullUrl } from '@/services/getters/getFullUrl';
import { useAuthStore, useNetworkStore } from '@/store';
import { useErrors, useSession } from '@/helpers';
import type { AuthFormModel } from '@/types';

type AutoLoginConfig = {
  ips: string[];
  urls: string[];
  uri: string;
  host: string;
  id: string;
  companyId: string;
  login: string;
  password: string;
};

type UseAutoLogin = {
  enabled: () => Promise<boolean>;
  attempt: () => Promise<boolean>;
};

export function useAutoLogin(): UseAutoLogin {
  const authStore = useAuthStore();
  const networkStore = useNetworkStore();
  const { handleError } = useErrors();
  const config: AutoLoginConfig = {
    ips: import.meta.env.VITE_KLINIKUM_ERDING_IPS?.split('|') || [],
    urls: import.meta.env.VITE_KLINIKUM_ERDING_URLS?.split('|') || [],
    uri: import.meta.env.VITE_KLINIKUM_ERDING_URI || '',
    host: import.meta.env.VITE_KLINIKUM_ERDING_HOST || '',
    id: import.meta.env.VITE_KLINIKUM_ERDING_ID || '',
    companyId: import.meta.env.VITE_KLINIKUM_ERDING_COMPANY_ID || '',
    login: import.meta.env.VITE_KLINIKUM_ERDING_LOGIN || '',
    password: import.meta.env.VITE_KLINIKUM_ERDING_PASSWORD || '',
  };

  function _checkConfig(): boolean {
    const { ips, urls, uri, host, id, companyId, login, password } = config;

    if (ips.length === 0) {
      console.error('[ERROR] No auto login ips found in VITE_KLINIKUM_ERDING_IPS');
      return false;
    }

    if (urls.length === 0) {
      console.error('[ERROR] No auto login urls found in VITE_KLINIKUM_ERDING_URLS');
      return false;
    }

    if (!uri || !host || !id || !companyId || !login || !password) {
      console.error('[ERROR] Missing required Klinikum Erding environment variables for auto login.');
      return false;
    }

    return true;
  }

  async function enabled(): Promise<boolean> {
    if (!_checkConfig()) return false;

    const { ips, urls } = config;

    if (!urls.includes(window.location.origin)) {
      console.log('[INFO] Current origin does not match auto login urls');
      return false;
    }

    let currentIP = '';
    try {
      const ip = await $api.tool.getIPInfo();
      if (!ip) throw new Error('Failed to fetch IP address');
      currentIP = ip;
    } catch (e: any) {
      console.error('[ERROR] Error while fetching the IP address', e);
      handleError({
        show: true,
        error: e,
        message: `Error while fetching the IP address: ${e.message}`,
      });
      return false;
    }

    if (!ips.includes(currentIP)) {
      console.warn('[WARN] Current IP address does not match required IPs for auto login');
      return false;
    }

    return true;
  }

  async function attempt(): Promise<boolean> {
    if (!_checkConfig()) return false;

    const { login, password, uri, host, companyId } = config;

    const form: AuthFormModel = { login, password, uri, fullURL: getFullUrl(host) };

    if (!form.fullURL) {
      console.error('[ERROR] Failed to construct full URL for auto login from VITE_KLINIKUM_ERDING_HOST.');
      return false;
    }

    authStore.setHost(host);

    try {
      const userNetworks = await networkStore.networksByForm(form);
      if (!userNetworks?.length) {
        console.warn('[WARN] Auto login failed: No networks found for the provided credentials.');
        return false;
      }

      const targetNetwork = userNetworks.find((network) => String(network.id) === String(companyId));

      if (!targetNetwork) {
        console.warn(`[WARN] Auto login failed: Target network ID (${companyId}) not found in user networks.`);
        return false;
      }

      await useSession().changeNetwork([targetNetwork], false, form);
      return true;
    } catch (e: any) {
      console.error('[ERROR] Error during auto login attempt', e);
      return false;
    }
  }

  return { enabled, attempt };
}
